import * as THREE from 'three';
import {
  ORBIT_SEMI_MAJOR_AXIS,
  ORBIT_ECCENTRICITY_DEFAULT,
  ORBIT_SPEED_DEFAULT,
  KEPLER_MAX_ITERATIONS,
  KEPLER_TOLERANCE,
} from './constants';
import { OrbitalParams } from './types';

/**
 * Computes elliptical orbital motion using Keplerian mechanics.
 * The orbit lies in the XZ plane with the focus at the origin.
 */
export class OrbitalSystem {
  private meanAnomaly: number = 0;
  private trueAnomaly: number = 0;
  private params: OrbitalParams;

  constructor(params?: Partial<OrbitalParams>) {
    this.params = {
      semiMajorAxis: params?.semiMajorAxis ?? ORBIT_SEMI_MAJOR_AXIS,
      eccentricity: params?.eccentricity ?? ORBIT_ECCENTRICITY_DEFAULT,
      orbitalSpeed: params?.orbitalSpeed ?? ORBIT_SPEED_DEFAULT,
    };
  }

  /**
   * Solves Kepler's equation M = E - e·sin(E) for eccentric anomaly E
   * using Newton-Raphson iteration.
   */
  solveKeplerEquation(M: number, e: number): number {
    // Initial guess: E = M (good for low eccentricity)
    let E = M;

    for (let i = 0; i < KEPLER_MAX_ITERATIONS; i++) {
      const dE = (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
      E -= dE;

      if (Math.abs(dE) < KEPLER_TOLERANCE) {
        break;
      }
    }

    return E;
  }

  /**
   * Converts eccentric anomaly E to true anomaly θ.
   * θ = 2 * atan2(sqrt(1+e) * sin(E/2), sqrt(1-e) * cos(E/2))
   */
  eccentricToTrueAnomaly(E: number, e: number): number {
    return 2 * Math.atan2(
      Math.sqrt(1 + e) * Math.sin(E / 2),
      Math.sqrt(1 - e) * Math.cos(E / 2)
    );
  }

  /**
   * Computes the position on the ellipse for a given true anomaly θ.
   * Uses polar equation: r = a(1 - e²) / (1 + e·cos(θ))
   * Maps to XZ plane: x = r·cos(θ), z = r·sin(θ), y = 0
   */
  computePosition(theta: number): THREE.Vector3 {
    const { semiMajorAxis: a, eccentricity: e } = this.params;
    const r = (a * (1 - e * e)) / (1 + e * Math.cos(theta));
    const x = r * Math.cos(theta);
    const z = r * Math.sin(theta);
    return new THREE.Vector3(x, 0, z);
  }

  /**
   * Advances the orbit by deltaTime seconds.
   * Returns the new position as a Vector3 in the XZ plane.
   */
  update(deltaTime: number): THREE.Vector3 {
    // Advance mean anomaly uniformly
    this.meanAnomaly += this.params.orbitalSpeed * 2 * Math.PI * deltaTime;

    // Wrap modulo 2π
    this.meanAnomaly = this.meanAnomaly % (2 * Math.PI);
    if (this.meanAnomaly < 0) {
      this.meanAnomaly += 2 * Math.PI;
    }

    // Solve Kepler's equation to get eccentric anomaly
    const E = this.solveKeplerEquation(this.meanAnomaly, this.params.eccentricity);

    // Convert to true anomaly
    this.trueAnomaly = this.eccentricToTrueAnomaly(E, this.params.eccentricity);

    // Ensure true anomaly is in [0, 2π)
    if (this.trueAnomaly < 0) {
      this.trueAnomaly += 2 * Math.PI;
    }

    // Compute and return position
    return this.computePosition(this.trueAnomaly);
  }

  // --- Setters with clamping ---

  setEccentricity(e: number): void {
    this.params.eccentricity = Math.max(0, Math.min(0.99, e));
  }

  setOrbitalSpeed(speed: number): void {
    this.params.orbitalSpeed = Math.max(0.01, Math.min(2.0, speed));
  }

  setSemiMajorAxis(a: number): void {
    this.params.semiMajorAxis = Math.max(0.1, a);
  }

  // --- Getters ---

  getTrueAnomaly(): number {
    return this.trueAnomaly;
  }

  getMeanAnomaly(): number {
    return this.meanAnomaly;
  }

  getParams(): OrbitalParams {
    return { ...this.params };
  }
}
